import { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, Save, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Users, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const sanitize = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "identifiants";

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

const printBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed"; iframe.style.right = "0"; iframe.style.bottom = "0";
  iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "0";
  iframe.src = url;
  iframe.onload = () => setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) {} }, 250);
  document.body.appendChild(iframe);
  setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 60000);
};

/**
 * Génération d'un vrai PDF (serveur) des identifiants parents.
 * Mode classe : props `classId` — récupère le résumé et régénère les mots de passe manquants.
 * Mode liste  : props `families` (assistant de rentrée multi-classes).
 */
export function CredentialsPrint({ open, onClose, classId, families: familiesProp, className, title }) {
  const isClassMode = !!classId;
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [summary, setSummary] = useState(null);
  const [families, setFamilies] = useState([]);
  const [meta, setMeta] = useState({ class_name: className || "", school_year: "" });
  const [siteUrl, setSiteUrl] = useState(typeof window !== "undefined" ? window.location.origin : "");

  const fetchClass = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/classes/${classId}/credentials`);
      setFamilies(data.families || []);
      setSummary(data.summary || null);
      setMeta({ class_name: data.class_name || "", school_year: data.school_year || "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (!open) return;
    if (isClassMode) {
      fetchClass();
    } else {
      const fams = familiesProp || [];
      setFamilies(fams);
      setSummary({
        total_parents: fams.length,
        ready: fams.filter((f) => f.temp_password).length,
        missing_password: fams.filter((f) => !f.temp_password).length,
        missing_email: fams.filter((f) => !(f.email)).length,
      });
      setMeta({ class_name: className || "", school_year: "" });
    }
  }, [open, isClassMode, fetchClass, familiesProp, className]);

  const filename = `Identifiants_parents_${sanitize(meta.class_name || title || "familles")}${meta.school_year ? "_" + sanitize(meta.school_year) : ""}.pdf`;

  const fetchPdf = async () => {
    if (isClassMode) {
      const { data } = await api.get(`/classes/${classId}/credentials/pdf`, {
        params: { login_url: siteUrl }, responseType: "blob",
      });
      return data;
    }
    const payload = {
      title: title || "Identifiants de connexion",
      login_url: siteUrl,
      families: families.map((f) => ({
        student_names: f.student_names || (f.student_name ? [f.student_name] : []),
        guardian_name: f.guardian_name || "",
        identifiant: f.identifiant || "",
        temp_password: f.temp_password || null,
      })),
    };
    const { data } = await api.post(`/credentials/pdf`, payload, { responseType: "blob" });
    return data;
  };

  const onDownload = async () => {
    setBusy("download");
    try {
      const blob = await fetchPdf();
      triggerDownload(blob, filename);
      toast.success("PDF enregistré.");
    } catch (err) {
      toast.error("Génération du PDF impossible.");
    } finally { setBusy(""); }
  };

  const onPrint = async () => {
    setBusy("print");
    try {
      const blob = await fetchPdf();
      printBlob(blob);
    } catch (err) {
      toast.error("Impression impossible.");
    } finally { setBusy(""); }
  };

  const regenerate = async (parentIds) => {
    setBusy("regen");
    try {
      const { data } = await api.post(`/classes/${classId}/credentials/regenerate`, parentIds ? { parent_ids: parentIds } : {});
      setFamilies(data.families || []);
      setSummary(data.summary || null);
      toast.success(`${data.regenerated} mot(s) de passe régénéré(s).`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const noReady = !summary || summary.ready === 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-h-[90vh] overflow-hidden rounded-2xl sm:max-w-[620px]" data-testid="credentials-print">
        <DialogHeader>
          <DialogTitle>Identifiants des familles</DialogTitle>
          <DialogDescription>
            Générez un PDF prêt à imprimer et à distribuer — une fiche par famille.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="credentials-loading">
            <Loader2 className="animate-spin text-slate-400" size={26} />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Résumé */}
            <div className="flex flex-wrap items-center gap-2" data-testid="credentials-summary">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-white/10 dark:text-slate-200">
                <Users size={13} /> {summary?.total_parents || 0} compte(s) parent(s)
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700" data-testid="credentials-ready">
                <CheckCircle2 size={13} /> {summary?.ready || 0} identifiant(s) prêt(s)
              </span>
              {summary?.missing_password > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700" data-testid="credentials-missing-password">
                  <AlertTriangle size={13} /> {summary.missing_password} sans mot de passe
                </span>
              )}
              {summary?.missing_email > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500" data-testid="credentials-missing-email">
                  <Mail size={13} /> {summary.missing_email} sans e-mail
                </span>
              )}
            </div>

            {/* Avertissement mots de passe manquants + régénération (mode classe) */}
            {isClassMode && summary?.missing_password > 0 && (
              <div className="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/5 dark:text-amber-300" data-testid="regen-banner">
                <p>{summary.missing_password} famille(s) ont déjà activé leur compte : le mot de passe initial n'est plus disponible.</p>
                <Button
                  variant="outline" size="sm"
                  className="w-fit rounded-lg border-amber-300 text-amber-800 hover:bg-amber-100"
                  disabled={busy === "regen"} onClick={() => regenerate(null)}
                  data-testid="regen-missing"
                >
                  {busy === "regen" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
                  Régénérer les mots de passe manquants
                </Button>
              </div>
            )}

            {/* Adresse du site de connexion */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Adresse du site de connexion (affichée sur les fiches)</Label>
              <Input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} className="h-9 rounded-lg" data-testid="credentials-site-url" placeholder="https://…" />
            </div>

            {/* Liste des familles */}
            <div className="max-h-[38vh] overflow-y-auto rounded-xl border border-slate-100 dark:border-white/10" data-testid="credentials-list">
              {families.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">Aucun compte parent pour cette liste.</p>
              ) : (
                <ul className="divide-y divide-slate-50 dark:divide-white/5">
                  {families.map((f, i) => (
                    <li key={f.parent_id || i} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`credentials-family-${i}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-slate-800 dark:text-slate-100">{(f.student_names || [f.student_name]).filter(Boolean).join(", ")}</p>
                        <p className="truncate text-xs text-slate-400">{f.guardian_name} · {f.identifiant}</p>
                      </div>
                      {f.temp_password ? (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Prêt</span>
                      ) : isClassMode ? (
                        <button
                          onClick={() => regenerate([f.parent_id])}
                          disabled={busy === "regen"}
                          className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
                          data-testid={`regen-family-${i}`}
                        >
                          <RefreshCw size={11} /> Régénérer
                        </button>
                      ) : (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Déjà changé</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-xl" disabled={!!busy || noReady} onClick={onDownload} data-testid="credentials-download">
                {busy === "download" ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                Enregistrer le PDF
              </Button>
              <Button className="rounded-xl" disabled={!!busy || noReady} onClick={onPrint} data-testid="credentials-print-button">
                {busy === "print" ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Printer size={16} className="mr-2" />}
                Imprimer
              </Button>
            </DialogFooter>
            {noReady && <p className="text-center text-xs text-slate-400">Aucun identifiant disponible à imprimer pour le moment.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
